import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Users, Phone, Mail, Calendar, MapPin, Building2, Trash2, ChevronDown } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import AdminSearch from './AdminSearch';

const STATUSES = [
  { key: 'all',       label: 'All',       color: 'gray'    },
  { key: 'new',       label: 'New',       color: 'blue'    },
  { key: 'contacted', label: 'Contacted', color: 'amber'   },
  { key: 'quoted',    label: 'Quoted',    color: 'violet'  },
  { key: 'won',       label: 'Won',       color: 'emerald' },
  { key: 'lost',      label: 'Lost',      color: 'rose'    },
];

const STATUS_STYLE = {
  new:       'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted:    'bg-violet-50 text-violet-700 border-violet-200',
  won:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost:      'bg-rose-50 text-rose-700 border-rose-200',
};

const fmtDate = (v) => {
  try { return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch (_) { return v || '—'; }
};

const GroupEnquiriesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await axios.get(`${API}/admin/group-enquiries`, { headers: adminAuthHeader() });
      setItems(res.data || []);
    } catch (e) {
      setErr('Failed to load group enquiries');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const prev = items;
    setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await axios.patch(`${API}/admin/group-enquiries/${id}`, { status }, { headers: adminAuthHeader() });
    } catch (_) { setItems(prev); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this group enquiry?')) return;
    const prev = items;
    setItems((list) => list.filter((it) => it.id !== id));
    try {
      await axios.delete(`${API}/admin/group-enquiries/${id}`, { headers: adminAuthHeader() });
    } catch (_) { setItems(prev); }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.slice(1).forEach((s) => { c[s.key] = 0; });
    items.forEach((it) => { c[it.status || 'new'] = (c[it.status || 'new'] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let list = filter === 'all' ? items : items.filter((it) => (it.status || 'new') === filter);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((it) => {
        const hay = [it.name, it.phone, it.email, it.destination, it.occasion, it.city, it.notes]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(s);
      });
    }
    return list;
  }, [items, filter, q]);

  return (
    <div data-testid="group-enquiries-manager">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={22} className="text-[#0d64a8]" /> Group Enquiries
          </h1>
          <p className="text-sm text-gray-500">Bulk-booking requests captured from the Group Booking tab on the home page.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0d64a8] px-3 py-1.5 rounded-md hover:bg-gray-50" data-testid="group-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search name, phone, email, destination, occasion..." testId="group-search" />
      </div>


      {/* Stat filters */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            data-testid={`group-filter-${s.key}`}
            className={`text-left border rounded-lg px-3 py-2 transition ${filter === s.key ? 'border-[#0d64a8] bg-[#e8f2fb] shadow-sm' : 'border-gray-200 bg-white hover:border-[#0d64a8]/50'}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-gray-500">{s.label}</div>
            <div className="text-xl font-bold text-gray-900">{counts[s.key] ?? 0}</div>
          </button>
        ))}
      </div>

      {err && <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2 rounded-md mb-4">{err}</div>}

      {loading ? (
        <div className="text-center py-16 text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 bg-white border border-gray-200 rounded-xl">
          <Users className="mx-auto text-gray-300 mb-2" size={46} />
          <p className="text-gray-800 font-semibold">No group enquiries {filter !== 'all' ? `in "${filter}"` : 'yet'}</p>
          <p className="text-xs text-gray-500 mt-1">They'll appear here as soon as travellers submit the Group Booking form.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((it) => {
            const total = Number(it.pax_adults || 0) + Number(it.pax_children || 0);
            const open = openId === it.id;
            return (
              <div key={it.id} data-testid={`group-row-${it.id}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 flex flex-wrap items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">{it.name}</h3>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[it.status || 'new']}`}>{it.status || 'new'}</span>
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">{it.group_type || 'other'}</span>
                      <span className="text-[10px] text-gray-500">· {fmtDate(it.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-1.5 text-[12px] text-gray-600">
                      <a href={`tel:${it.phone}`} className="flex items-center gap-1 hover:text-[#0d64a8]"><Phone size={11}/> {it.phone}</a>
                      {it.email && <a href={`mailto:${it.email}`} className="flex items-center gap-1 hover:text-[#0d64a8]"><Mail size={11}/> {it.email}</a>}
                      {it.company && <span className="flex items-center gap-1"><Building2 size={11}/> {it.company}</span>}
                      {it.destination && <span className="flex items-center gap-1"><MapPin size={11}/> {it.destination}</span>}
                      {(it.travel_from || it.travel_to) && (
                        <span className="flex items-center gap-1"><Calendar size={11}/> {it.travel_from || '?'} → {it.travel_to || '?'}</span>
                      )}
                      <span className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5"><Users size={11}/> {total} pax ({it.pax_adults}A · {it.pax_children}C)</span>
                      {Number(it.budget_per_person) > 0 && (
                        <span className="text-[#0d64a8] font-medium">Budget: INR {Math.round(Number(it.budget_per_person)).toLocaleString('en-IN')} / person</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={it.status || 'new'}
                      onChange={(e) => setStatus(it.id, e.target.value)}
                      data-testid={`group-status-${it.id}`}
                      className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:border-[#0d64a8]"
                    >
                      {STATUSES.slice(1).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <button onClick={() => setOpenId(open ? null : it.id)} className="text-xs text-gray-600 hover:text-[#0d64a8] flex items-center gap-1 px-2 py-1.5 rounded-md border border-gray-200 hover:border-[#0d64a8]" data-testid={`group-expand-${it.id}`}>
                      <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`}/> Details
                    </button>
                    <button onClick={() => remove(it.id)} className="text-red-500 hover:bg-red-50 rounded-md p-1.5 border border-transparent hover:border-red-200" data-testid={`group-delete-${it.id}`}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 text-xs text-gray-700 space-y-2">
                    <div>
                      <span className="text-gray-500 font-medium uppercase tracking-widest text-[10px]">Services requested</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(it.inclusions_needed || []).length === 0 ? (
                          <span className="text-gray-400 italic">—</span>
                        ) : (it.inclusions_needed || []).map((s) => (
                          <span key={s} className="bg-white border border-gray-200 rounded px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                    </div>
                    {it.message && (
                      <div>
                        <span className="text-gray-500 font-medium uppercase tracking-widest text-[10px]">Notes</span>
                        <p className="mt-1 whitespace-pre-line">{it.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroupEnquiriesManager;
